import { useState } from 'react';
import {
  IconButton,
  ListItemText,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { observer } from 'mobx-react-lite';

import { useDirection,useInjection } from '@hooks';
import { useTrans } from '@libs/i18n';
import { CaretDownIcon } from '@phosphor-icons/react';
import { LocalStorageStore } from '@stores';
import { NoFlipMenu } from '@utils';

import { DI_TOKENS } from '../constants';

const languages = {
  en: { name: 'English', short: 'EN', dir: 'ltr' as const },
  ar: { name: 'العربية', short: 'AR', dir: 'rtl' as const },
};

type LanguageKey = keyof typeof languages;

/**
 * Visual style of the switcher:
 * - `pill`     — compact EN / AR toggle pill (matches the marketing website and
 *                the Keycloak login theme). Used in the header and auth layout.
 * - `dropdown` — labelled dropdown menu. Used in Settings > Language.
 */
export type LanguageSwitcherVariant = 'pill' | 'dropdown';

interface LanguageSwitcherProps {
  variant?: LanguageSwitcherVariant;
}

/**
 * Language switcher (English / العربية). Owns the shared switching logic —
 * changes the i18next language and persists both the language and the resolved
 * text direction to the LocalStorageStore so the MUI theme reacts to it — and
 * renders one of two visual variants.
 */
export const LanguageSwitcher = observer(({ variant = 'pill' }: LanguageSwitcherProps) => {
  const { i18n } = useTrans();
  const queryClient = useQueryClient();
  const store = useInjection<LocalStorageStore>(DI_TOKENS.LocalStorageStore);
  const currentLang: LanguageKey = languages[store.lang as LanguageKey] ? store.lang : 'en';

  const switchLang = (lang: LanguageKey) => {
    if (!languages[lang] || lang === currentLang) return;
    store.set('direction', languages[lang].dir);
    store.set('lang', lang);
    // The API resolves localized content (domain/document names, descriptions,
    // etc.) server-side from the `Accept-Language` header. Once the language
    // switch has persisted the new locale, invalidate every query so the next
    // request refetches in the new language instead of serving the stale,
    // first-loaded one — a single place instead of keying each query on locale.
    i18n.changeLanguage(lang).then(() => queryClient.invalidateQueries());
  };

  if (variant === 'dropdown') {
    return (
      <LanguageDropdown
        currentLang={currentLang}
        onSelect={switchLang}
      />
    );
  }

  return <LanguagePill currentLang={currentLang} onSelect={switchLang} />;
});

interface VariantProps {
  currentLang: LanguageKey;
  onSelect: (lang: LanguageKey) => void;
}

const LanguagePill = observer(({ currentLang, onSelect }: VariantProps) => (
  <ToggleButtonGroup
    value={currentLang}
    exclusive
    onChange={(_, value: LanguageKey | null) => value != null && onSelect(value)}
    size="small"
    sx={{
      bgcolor: 'grey.200',
      borderRadius: '50px',
      px: 0.5,
      py: 0.5,
      minWidth: 80,
      gap: 1,
      '& .MuiToggleButtonGroup-grouped': {
        border: 'none',
        borderRadius: '50px',
        px: 1.25,
        py: 0.375,
        minWidth: 36,
        flex: 1,
        fontSize: '0.8125rem',
        fontWeight: 700,
        '&:not(.Mui-selected)': {
          bgcolor: 'transparent',
          color: 'text.secondary',
          '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
        },
        '&.Mui-selected': {
          bgcolor: 'background.paper',
          color: 'text.primary',
          boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
          '&:hover': { bgcolor: 'background.paper' },
        },
      },
    }}
  >
    {(Object.keys(languages) as LanguageKey[]).map((lang) => (
      <ToggleButton key={lang} value={lang} aria-label={languages[lang].name}>
        {languages[lang].short}
      </ToggleButton>
    ))}
  </ToggleButtonGroup>
));

const LanguageDropdown = observer(({
  currentLang,
  onSelect,
}: VariantProps) => {
  const { dir } = useDirection();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const current = languages[currentLang];

  const handleSelect = (lang: LanguageKey) => {
    onSelect(lang);
    setAnchor(null);
  };

  return (
    <>
      <IconButton
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 1.5,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'black.100',
          backgroundColor: 'background.paper',
          justifyContent: 'space-between',
          '&:hover': { backgroundColor: 'grey.100' },
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {current.name}
        </Typography>
        <CaretDownIcon size={16} weight="bold" />
      </IconButton>

      <NoFlipMenu
        mode={dir}
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          },
        }}
      >
        {(Object.keys(languages) as LanguageKey[]).map((lang) => (
          <MenuItem
            key={lang}
            onClick={() => handleSelect(lang)}
            selected={currentLang === lang}
            sx={{
              py: 1.5,
              pl: 3,
              '&.Mui-selected': { backgroundColor: 'primary.light' },
            }}
          >
            <ListItemText sx={{ textAlign: 'center' }}>
              <Typography variant="body2">{languages[lang].name}</Typography>
            </ListItemText>
          </MenuItem>
        ))}
      </NoFlipMenu>
    </>
  );
});
